import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api.js";
import { useQueryClient } from "@tanstack/react-query";
import ScheduleGrid from "./ScheduleGrid";

export interface Office {
  id: number;
  professional?: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  availability?: {
    day: string;
    startTime: string;
    endTime: string;
    duration: number;
  }[];
}

const OfficeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [office, setOffice] = useState<Office | null>(null);
  const [schedule, setSchedule] = useState<
    {
      day: string;
      startTime: string;
      endTime: string;
      duration: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    document.title = "Editar consultorio";
    const fetchOffice = async () => {
      try {
        const response = await api.get(`/offices/${id}/`);
        setOffice(response.data);
        if (response.data.availability) {
          setSchedule(response.data.availability);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch office");
      } finally {
        setLoading(false);
      }
    };

    fetchOffice();
  }, [id]);

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await api.delete(`/offices/${id}/`);
      await queryClient.invalidateQueries({
        queryKey: ["offices"],
        refetchType: "active",
      });
      navigate("/offices");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete office");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // const DeleteConfirmationModal = () => (
  //   <div className="modal-overlay">
  //     <div className="modal-container">
  //       <h3>Confirmar Eliminación</h3>
  //       <p>
  //         ¿Estás seguro de que deseas eliminar este consultorio? Todos los datos
  //         asociados serán eliminados permanentemente y esta acción no se puede
  //         deshacer.
  //       </p>
  //       <div className="modal-actions">
  //         <button
  //           onClick={() => setShowDeleteModal(false)}
  //           className="new-cancel-button"
  //         >
  //           Cancelar
  //         </button>
  //         <button
  //           onClick={handleDeleteConfirm}
  //           disabled={deleting}
  //           className="new-delete-button"
  //         >
  //           {deleting ? "Eliminando..." : "Sí, eliminar"}
  //           <svg
  //             width="24"
  //             height="24"
  //             viewBox="0 0 24 24"
  //             fill="none"
  //             xmlns="http://www.w3.org/2000/svg"
  //           >
  //             <path
  //               d="M18 5V19C18 19.5 17.5 20 17 20H12H7C6.5 20 6 19.5 6 19V5"
  //               stroke="#757575"
  //               stroke-width="2"
  //               stroke-linecap="round"
  //               stroke-linejoin="round"
  //             />
  //             <path
  //               d="M4 5H20"
  //               stroke="#757575"
  //               stroke-width="2"
  //               stroke-linecap="round"
  //               stroke-linejoin="round"
  //             />
  //             <path
  //               d="M10 4H14M10 9V16M14 9V16"
  //               stroke="#757575"
  //               stroke-width="2"
  //               stroke-linecap="round"
  //               stroke-linejoin="round"
  //             />
  //           </svg>
  //         </button>
  //       </div>
  //     </div>
  //   </div>
  // );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Cargando consultorio...</p>
      </div>
    );
  }
  if (error) return <div>Error: {error}</div>;
  if (!office) return <div>Office not found</div>;

  return (
    <div className="patient-detail-container">
      <div className="detail-header">
        <h2 className="title-office">{office.name}</h2>
        <div className="detail-actions">
          <button
            onClick={() => navigate(`/offices/${id}/edit`)}
            className="new-save-button"
          >
            Editar
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="new-delete-button"
          >
            Borrar
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 5V19C18 19.5 17.5 20 17 20H12H7C6.5 20 6 19.5 6 19V5"
                stroke="#757575"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4 5H20"
                stroke="#757575"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M10 4H14M10 9V16M14 9V16"
                stroke="#757575"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            onClick={() => navigate("/offices")}
            className="new-cancel-button"
          >
            Regresar
          </button>
        </div>
      </div>

      <div className="patient-info">
        {office.address && (
          <div className="info-group">
            <label>Dirección:</label>
            <p>{office.address}</p>
          </div>
        )}

        {(office.city || office.state || office.country) && (
          <div className="info-group">
            <label>Ubicación:</label>
            <p>
              {[office.city, office.state, office.country]
                .filter(Boolean)
                .join(", ")}
            </p>
          </div>
        )}

        {office.phone && (
          <div className="info-group">
            <label>Teléfono:</label>
            <p>{office.phone}</p>
          </div>
        )}

        {office.email && (
          <div className="info-group">
            <label>Email:</label>
            <p>{office.email}</p>
          </div>
        )}

        {office.website && (
          <div className="info-group">
            <label>Sitio web:</label>
            <p>
              <a
                href={office.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {office.website}
              </a>
            </p>
          </div>
        )}
      </div>
      <div className="dashboard-section">
        <ScheduleGrid scheduleItems={schedule} />
      </div>
    </div>
  );
};

export default OfficeDetail;
